import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Anchor, Chip, SwitchField } from "@exopengithub/exokit";
import { PlanningTable } from "src/features/planning/components/PlanningTable.js";
import { ApiKeyValue } from "./ApiKeyValue";
import type { ApiKey } from "./apiKeyTypes";

type LegalEntityRef = { id: string; name: string };

interface ApiKeysTableProps {
	apiKeys: ApiKey[];
	companyDomainId: string;
	legalEntityByMasterKey: Map<string, LegalEntityRef>;
	onToggleActive: (apiKey: ApiKey, nextActive: boolean) => void;
	isToggling?: boolean;
}

export const ApiKeysTable = ({
	apiKeys,
	companyDomainId,
	legalEntityByMasterKey,
	onToggleActive,
	isToggling = false,
}: ApiKeysTableProps) => {
	const { t } = useTranslation();

	return (
		<PlanningTable ariaLabel={t("API keys")}>
			<thead>
				<tr>
					<th>{t("Source key")}</th>
					<th>{t("Legal entity")}</th>
					<th>{t("API key")}</th>
					<th>{t("Status")}</th>
				</tr>
			</thead>
			<tbody>
				{apiKeys.map((apiKey) => {
					const legalEntity = apiKey.leMasterKey
						? legalEntityByMasterKey.get(apiKey.leMasterKey)
						: undefined;
					return (
						<tr key={apiKey.apiKey}>
							<td>{apiKey.sourceKey}</td>
							<td>
								{legalEntity ? (
									<Anchor
										component={Link}
										to={`/company-domains/${companyDomainId}/legal-entities/${legalEntity.id}/info`}
									>
										{legalEntity.name}
									</Anchor>
								) : apiKey.leMasterKey ? (
									<Chip size="small" variant="error">
										{t("Legal entity not found")}
									</Chip>
								) : (
									<Chip size="small" variant="gray">
										{t("No legal entity")}
									</Chip>
								)}
							</td>
							<td>
								<ApiKeyValue value={apiKey.apiKey} />
							</td>
							<td>
								<SwitchField
									label={
										apiKey.active
											? t("Active ({{sourceKey}})", {
													sourceKey: apiKey.sourceKey,
												})
											: t("Inactive ({{sourceKey}})", {
													sourceKey: apiKey.sourceKey,
												})
									}
									labelProps={{ className: "sr-only" }}
									checked={apiKey.active}
									disabled={isToggling}
									onChange={() => onToggleActive(apiKey, !apiKey.active)}
								/>
								<span aria-hidden className="ml-2 text-sm">
									{apiKey.active ? t("Active") : t("Inactive")}
								</span>
							</td>
						</tr>
					);
				})}
			</tbody>
		</PlanningTable>
	);
};
